import type { NamespaceSharedTranslation } from '../../i18n-types';

const da_shared = {
  accessGuard: {
    confirmText: 'Få adgang',
    error: {
      gainAccess: 'Fejl ved at få adgang til patient',
    },
    inputPlaceholder: 'Hvorfor har du brug for adgang til denne patient?',
    message:
      'Du har ikke adgang til denne patient, Du kan få midlertidig adgang i 24 timer.',
    rejectText: 'Gå tilbage',
    title: 'Du har ikke adgang',
  },
  forbiddenPatient: {
    title: 'Ups, vi kan ikke bekræfte din adgang til patienten.',
    body: 'Arbejder du i flere klinikker, er det også vigtigt, at du er logget på den korrekte klinik.',
    nspVerification:
      'Din adgang bygger på en behandlerrelation, som vi tjekker via de nationale systemer.\n' +
      'Dvs. forsøger du f.eks. at anvende Dermloop på en kollega, familiemedlem eller anden, som du ikke har behandlerrelation til, er dette desværre ikke muligt.',
    contactSupport:
      'Hvis du mener, du bør have adgang til denne patient, så ring endelig til vores support.',
    supportPhoneLabel: 'Tlf:',
    contactSupportButton: 'Kontakt support',
    goBack: 'Gå tilbage',
  },
  alertMessages: {
    anErrorOccurred: 'Der skete en fejl',
    cantBeUndone: 'Denne handling kan ikke fortrydes.',
    comingSoon: 'Kommer snart!',
    confirmDelete: 'Er du sikker på, at du vil slette dette?',
    confirmDeleteLesion:
      'Er du sikker på, du vil slette denne læsion? Denne handling kan ikke fortrydes.',
    confirmDeleteRash:
      'Er du sikker på, du vil slette dette hudlidelse? Denne handling kan ikke fortrydes.',
    confirmDeleteDraft:
      'Er du sikker på, du vil slette denne kladde? Tidligere registreringer på denne sag bevares.',
    deleteLesion: 'Slet læsion?',
    deleteRash: 'Slet hudlidelse?',
    deleteDraft: 'Slet kladde?',
    deletionError: 'Sletning mislykkedes',
    deletionSuccess: 'Sletning gennemført',
    imageNotSaved:
      '{imagetype} billede ikke gemt pga. en fejl. Tilføj nyt billede hvis nødvendigt.',
    theLesionIsEmpty: 'Denne læsion er tom',
    theRashIsEmpty: 'Hudlidelse er tomt',
    wouldYouLikeToDelete: 'Vil du slette den?',
  },
  assessmentTypes: {
    histopathologicalEvaluation: 'Histopatologisk vurdering',
    teleEvaluation: 'Televurdering',
  },
  authentication: {
    mitid: {
      login: 'Log ind med MitID',
      newUser: 'Ny bruger',
      notYet: 'Ikke nu',
      title: 'MitID i Dermloop',
      errorTitle: 'Forkert MitID',
      signinWithOtherAcc: 'Log ind med en anden MitID',
      wrongMitId:
        'Du har valgt en forkert MitID konto. CVR nummeret matcher ikke det fra din kliniske enhed.',
      subtitle: 'Vi er i gang med at implementere MitID i Dermloop. ',
      oneLoginSubtitle: 'Du er nu logget ind via dit journalsystem.',
      oneLoginConnectAccounts:
        'For at fortsætte skal du nu forbinde med det MitID Erhverv, der er tilknyttet den klinik, hvor du arbejder. Dette trin sikrer, at vi kan forbinde din konto korrekt.',
      connectAccounts:
        'Derfor beder vi dig venligst om at forbinde din Dermloop-konto med MitID. Vi beklager ulejligheden.',
      notYetConnected:
        'Du har ikke forbundet din Dermloop-konto med MitID endnu. Hvis din kliniske enhed har slået MitID til vil vi bede dig om at logge ind som normalt og følge migrationstrinene før du kan logge ind med MitID. Ellers skal du kontakte support',
      goBack: 'Gå tilbage',
      migrate: 'Forbind MitID',
      migratingUser: 'Forbinder {user} til denne Dermloop konto',
    },
    desktopQrLogin: {
      errorMessage:
        'Der opstod et problem ved login. Hvis problemet fortsætter, kontakt support: +45 44 40 82 45',
      alreadyRedeemedErrorMessage:
        'Denne kode er allerede blevet brugt til at logge ind. Hvis det ikke var dig, bedes du kontakte support: +45 44 40 82 45',
    },
    errors: {
      fetchingUser: 'Der opstod en fejl under hentning af bruger',
      internalServer: 'Intern serverfejl. Prøv igen senere.',
      websocketConnection: 'Ingen forbindelse til websocket-server',
    },
    inputs: {
      email: {
        label: 'Email',
      },
      firstName: {
        label: 'Fornavn',
      },
      lastName: {
        label: 'Efternavn',
      },
      missingValueMessage: 'Udfyld venligst {label}',
      password: {
        label: 'Adgangskode',
      },
      phonenumber: {
        label: 'Telefon',
        validation: {
          invalidForPrefix: 'Telefonnummer matcher ikke landekoden',
        },
      },
      termsAndConditions: {
        label: 'Email',
      },
    },
    screens: {
      invalidLoginMethod: {
        title: 'Ugyldig loginmetode',
        description:
          'Den valgte loginmetode er ikke længere tilgængelig. Login med MitID/SEB, eller tilgå Dermloop via Klinik+ i dit journalsystem. Kontakt support, hvis du har brug for hjælp.',
        button: 'Log ud og prøv igen',
      },
      approveDeviceSession: {
        description:
          'Godkend adgang til enhed ved at trykke på de bogstaver, der matcher bogstaverne på den enhed, du er ved at autentificere.',
        errorResult: {
          text: 'Der opstod en fejl under autentificering af enhedens session. Scan venligst QR-koden igen.',
          title: 'Enhed ikke autentificeret',
        },
        errors: {
          gettingDeviceSession:
            'Der opstod en fejl under hentning af enhedens session',
          missingAuthCode: 'Ingen kode blev angivet i URL-parametre',
          missingSessionId: 'Intet sessionId blev angivet i URL-parametre',
        },
        successResult: {
          text: 'Du kan nu lukke dette vindue.',
          title: 'Enhed autentificeret',
        },
        title: 'Autentificer enhed',
      },
      changePassword: {
        inputs: {
          confirmNewPassword: {
            label: 'Bekræft ny adgangskode',
            validation: {
              passwordMismatch: 'Adgangskoderne er ikke ens',
            },
          },
          newPassword: {
            label: 'Ny adgangskode',
          },
        },
        reasons: {
          newLoginSystem:
            'Dermloop migrerer til et nyt loginsystem. Vi beder dig derfor venligst skifte til en ny adgangskode.',
        },
        submitText: 'Skift adgangskode',
        successResult: {
          recoveryModeOnlyText: 'Du kan nu lukke denne skærm',
          text: 'Du kan nu tilgå appen ved at klikke på knappen herunder.',
          title: 'Din adgangskode er blevet skiftet.',
        },
        title: 'Skift adgangskode',
        titleSucess: 'Adgangskoden er nu skiftet',
      },
      completeProfile: {
        title: 'Færdiggør din profil',
        firstName: 'Fornavn',
        firstNamePlaceholder: 'Jens',
        lastName: 'Efternavn',
        lastNamePlaceholder: 'Jensen',
        email: 'Personlig emailadresse - Så vi kan kontakte dig',
        emailPlaceholder: 'jens.jensen@eksempel.com',
        continueButton: 'Fortsæt',
        helpUsText:
          'Tak fordi du hjælper os med at udfylde din manglende information',
        errors: {
          thisIsRequired: 'Påkrævet',
          errorUpdatingUser:
            'Noget gik galt da vi prøvede at opdatere din profil',
        },
      },
      createAccount: {
        existingAccountLinkText: 'Log ind',
        existingAccountText: 'Har du allerede en konto?',
        privacyPolicyLink: 'https://melatech.io/legal/privacy',
        privacyPolicyLinkText: 'Privatlivspolitik.',
        readTermsAndConditionsText: 'Jeg har læst og accepterer',
        submitText: 'Opret',
        subtitle:
          'Opret en konto for at tilgå vores case-bibliotek og meget mere.',
        termsAndConditionsLink: 'https://melatech.io/legal/terms',
        termsAndConditionsLinkText: 'Vilkår',
        termsAndConditionsSeperator: 'og',
        title: 'Opret konto',
      },
      login: {
        createAccount: 'Opret konto',
        creatingQRCode: 'Opretter QR-kode',
        dermKit: 'Dermkit',
        errors: {
          errorOccuredWhileCreatingQRCode:
            'Der opstod en fejl under oprettelse af QR-kode',
          incorrectEmailOrPassword: 'Forkert email eller adgangskode.',
          qrCodeExpired: 'QR-kode udløbet',
        },
        forgotPassword: 'Glemt adgangskode?',
        login: 'Log ind',
        loginError: 'Login mislykkedes',
        loginWithEmail: 'Skift til e-mail',
        loginWithGoogle: 'Log ind med Google',
        loginWithQRCode: 'Log ind med QR-kode',
        needAnAccount: 'Brug for en konto?',
        personalDevice: 'Personlig Enhed',
        qrCodeCountDown: 'Udløber om {time} minutter',
        resetQRCode: 'Nulstil QR-kode',
        scanQRCode: 'Log ind med QR-kode',
        subtitle: 'Velkommen tilbage',
      },
      medicalUnitInvitation: {
        accept: 'Acceptér',
        additionalRoles: 'Flere roller',
        invitedToMedicalUnit: 'Du er blevet inviteret til en klinisk enhed',
        reject: 'Afslå',
      },
      mfaConfirmation: {
        infoPopUp: {
          paragraph1:
            'Du kan logge ind uden SMS-verificering på en godkendt enhed.',
          paragraph2:
            'Godkendelsen er aktiv i 30 dage og skal derefter fornyes.',
          title: 'Godkend denne enhed',
        },
        inputs: {
          approveDevice: {
            label: 'Godkend denne enhed til login (30 dage)',
          },
          verificationCode: {
            label: 'Verificeringskode',
            validation: {
              invalidCode: 'Ugyldig kode',
            },
          },
        },
        paragraph1: 'Tjek din mobiltelefon',
        paragraph2: 'og indtast den kode, du har fået via sms:',
        resendText: 'Send en ny SMS',
        submitText: 'Verificér kode',
        editText: 'Redigér telefonnummer',
        title: 'Bekræft din identitet',
        noSmsTitle: 'Fik du ikke en SMS med bekræftelseskoden?',
        noSmsAction:
          'Tjek om telefonnummeret er korrekt, og prøv at sende en ny SMS med bekræftelseskode.',
      },
      mfaEnrollment: {
        paragraph1: 'Vi skal bekræfte din identitet.',
        paragraph2:
          'Vi sender en sekscifret kode til dit telefonnummer til bekræftelse.',
        submitText: 'Bekræft telefonnummer',
        title: 'Tilføj dit telefonnummer',
      },
      pickMedicalUnit: {
        subtitle: 'Vælg den kliniske enhed, du vil tilgå',
        successMessage: 'Skiftet til klinisk enhed {medicalUnitName}',
        title: 'Vælg klinisk enhed',
        noAccessMessage:
          'Du har ikke adgang til nogen kliniske enheder med kontoen {username}. Kontakt din lokale superbruger eller skriv til Melatechs support på support@melatech.io',
      },
      recovery: {
        checkYourMail:
          'Tjek din indbakke og indtast den kode, vi har sendt dig på',
        emailInstructions:
          'Indtast din emailadresse, så sender vi dig instruktioner til at nulstille din adgangskode.',
        submitCheckCode: 'Bekræft identitet',
        submitEmail: 'Send gendannelseskode',
        title: 'Gendan konto',
      },
    },
    slowLoadingText: 'Dette tager længere end normalt.',
    signingOut: 'Logger ud…',
    logout: 'Log ud',
  },
  dataTable: {
    filters: 'Filtre',
    clearAllFilters: 'Nulstil',
    noActiveFilters: 'Ingen aktive filtre',
    removeFilter: 'Fjern {category}:',
    searchOptions: 'Søg',
    noMatchingOptions: 'Ingen resultater',
  },
  buttons: {
    add: 'Tilføj',
    back: 'Tilbage',
    cancel: 'Annuller',
    changeMedicalUnit: 'Skift klinisk enhed',
    clear: 'Ryd',
    close: 'Luk',
    collapse: 'Fold sammen',
    completeEntry: 'Fuldfør registrering',
    confirm: 'Bekræft',
    continue: 'Fortsæt',
    delete: 'Slet',
    deleteAccount: 'Slet konto',
    edit: 'Redigér',
    filter: 'Filtrér',
    help: 'Hjælp',
    leave: 'Forlad',
    patientOverview: 'Patientoverblik',
    remove: 'Fjern',
    reset: 'Nulstil',
    restore: 'Gendan',
    save: 'Gem',
    saveAndContinue: 'Gem og fortsæt',
    saveChanges: 'Gem ændringer',
    saveDraft: 'Gem kladde',
    saveDraftDisabled:
      'Kun den der oprettede kladden kan gemme eller slette den. Brug den grønne knap for at fuldfølge registreringen selv.',
    saveDraftDisabledAssistant:
      'Kun den der oprettede kladden kan gemme eller slette den.',
    send: 'Send',
    sendPathology: 'Send til patolog',
    sendTeleReferral: 'Anmod om televurdering',
    signIn: 'Log ind',
    signOut: 'Log ud',
    read: 'Læs',
    skip: 'Spring over',
    tryAgain: 'Prøv igen',
  },
  clinicalActions: {
    biopsy: 'Biopsi',
    biopsyAction: 'Biopsitype',
    biopsyActionText:
      '{biopsyTypeName|{Curretage: Curretage, Excision: Excision, Incision: Incision, Punch: Stansebiopsi, Shave: Shave, *: Unspecified}} {text}',
    clinicalAction: 'Klinisk handling',
    dismissal: 'Afslutning',
    followUpAssessment: 'Opfølgning',
    newImageRegistration: 'Ny billedregistrering',
    noAction: 'Ingen handling',
    notSpecified: 'Ikke specificeret',
    physical: 'Fysisk',
    plannedBiopsy: 'Biopsi planlægges',
    referral: 'Henvisning',
    referralActionText:
      '{referralType|{physical: Fysisk henvisning, *: Anmodning om televurdering}}',
    tele: 'Tele',
    topicalTreatment: 'Topikal behandling',
    noTreatment: 'Ingen behandling',
    scraping: 'Skrab / podning',
    cryotherapy: 'Frysning',
    oralTreatment: 'P.O. behandling',
    other: 'Andet',
  },
  dateDayMonthFormat: 'dd.MM',
  dateFormat: 'dd.MM.yyyy',
  dateHoursFormat: 'dd.MM.yyyy HH:mm',
  hoursFormat: 'HH:mm',
  dateText: {
    daysFromBaseline: '{dateDiff} dag{{e}} fra baseline',
    followUpMonths: '{months} måned{{er}}',
    monthsFromBaseline: '{dateDiff} måned{{er}} fra baseline',
  },
  deleteUserRequest: {
    actionText: {
      cancel: 'Annuller',
      close: 'Luk',
      delete: 'Slet',
    },
    impactNote:
      'Bemærk, at dette sletter din konto i <>Dermloop Learn<>, <>Dermloop Desktop<> og <>Dermloop Capture<>.',
    processNote:
      'Kontosletning udføres manuelt af Melatech-teamet inden for en måned.',
    regretInstruction:
      'Hvis du fortryder din kontosletning, kan du logge ind på din konto inden din konto slettes. Du vil blive spurgt, om du ønsker at fortryde kontosletningen.',
    requestPrompt: 'Ønsker du at anmode om sletning af din konto?',
    requestRegistered:
      'Vi har registreret din anmodning om kontosletning. Vi sletter din konto <>inden for en måned<>.',
    title: {
      received: 'Anmodning modtaget',
      requested: 'Slet konto?',
    },
  },
  skinCheckRecency: {
    lessThanOneYear: 'Under 1 år',
    oneToFiveYears: '1 - 5 år',
    moreThanFiveYears: 'Mere end 5 år',
    never: 'Aldrig',
    unknown: 'Ved ikke',
  },
  descriptors: {
    action: 'Handling',
    addDifferentialDiagnosis: 'Tilføj differentialdiagnose',
    ageOfLesion: 'Læsionens alder',
    ageOfRash: 'Hvor længe har hudlidelse været til stede?',
    lastSkinCheck:
      'Hvornår fik du sidst tjekket din hud hos en læge eller hudlæge?',
    plannedSkinCheckWithinSixMonths:
      'Havde du planer om at få tjekket din hud hos læge eller hudlæge inden for de næste 6 måneder?',
    allLesions: 'Alle læsioner',
    allConditions: 'Alle tilstande',
    allUrgencies: 'Alle akutheder',
    archive: 'Arkiv',
    assessedBy: 'Vurderet af',
    changesInAppearance: 'Ændringer i udseende',
    clinicalDiagnosis: 'Klinisk diagnose',
    clinicalDiagnosisFromLesion: 'Hvad er din tentative diagnose af læsionen?',
    clinicalDiagnosisFromRash: 'Hvad er din tentative hudlidelsesdiagnose?',
    dateOfBirth: 'Fødselsdato',
    describeOther: 'Beskriv "Andet"',
    describeTopicalTreatment: 'Beskriv topikal behandling',
    differentialDiagnosis: 'Differentialdiagnose',
    dob: 'Født •',
    enterImmunosuppressantAndDate:
      'Angiv immunsuppresiv medicin og start/slut-dato',
    enterMedication: 'Angiv medicinering',
    enterTreatment: 'Angiv tidligere behandling/vurdering og resultat',
    excisionSize: 'Excisionens størrelse',
    familyHistoryOfMelanoma: 'Melanom i familien',
    fetchingPatient: 'Henter nyeste patientinformation',
    formerSkinCancer: 'Tidligere hudkræft',
    formerTreatments: 'Tidligere behandlinger',
    formerTreatmentsFromLesion:
      'Har læsionen tidligere undergået nogen behandling eller vurdering?',
    formerTreatmentsFromRash:
      'Er hudlidelsen tidligere blevet forsøgt behandlet? (orale/topikale steroider, antihistaminer etc.)',
    formerTreatmentsOrTraumas: 'Tidligere behandling{{nr:er}}',
    fromLesion: 'Fra læsion',
    hasAggravatingFactors:
      'Forværres hudlidelsen af noget? (kulde, stress etc.)',
    hasAlleviatingFactors:
      'Lindres hudlidelsen af noget? (soleksponering, fugtighedscreme etc.)',
    hasGeneralSymptoms:
      'Nye systemiske symptomer i forbindelse med hudlidelsen? (Feber, ledsmerter etc.)',
    hasMedicalOrExposure:
      'Nogen nye medikamenter eller eksponeringer forud for hudlidelsen begyndelse? (hudpleje/kosmetik, sæbe, rengøringsmidler etc.)',
    histologicalDiagnosis: 'Histologisk diagnose',
    histopathologicalEvaluation: 'Histopatologisk vurdering',
    image: 'billede',
    images: 'billede(r)',
    immunosuppressantUse:
      'Nuværende/tidligere brug af immunsuppressiv medicin?',
    immunosuppressantUseExample: '(fx efter organ/knoglemarvstransplantation)',
    inspection: 'ABCD-kriter',
    lastAccessed: 'Senest tilgået',
    lastClinicalAction: 'Nyeste klinisk handling',
    lastRegistration: 'Nyeste registrering',
    condition: 'Tilstand',
    lesionDoesntMatch: 'Læsion matcher ikke patient',
    lesionImageBaseline: 'Baseline',
    conditions: 'Tilstande',
    lesionThickness: 'Estimeret tykkelse',
    loading: 'Indlæser',
    malignancy: 'Malignitet',
    medicalRecord: 'Journal',
    noChanges: 'Ingen forandringer',
    noLesion: 'Ingen læsion',
    noPatient: 'Ingen patient',
    noSymptoms: 'Ingen symptomer',
    note: 'Henvisningsårsag og yderligere læsionsdetaljer',
    eConsultNote: 'Årsag til anmodning om televurdering',
    caseSummaryNote: 'Yderligere information',
    nrImages: '{nr} billede{{r}}',
    nrMonths: '{nr} måned{{er}}',
    objectiveFindings: 'Objektive fund',
    optional: 'Valgfrit',
    other: 'Andet',
    otherAge: 'Anden alder',
    otherAggravatingFactors: 'Angiv øvrige ting, der forværrer hudlidelsen',
    otherAlleviatingFactors: 'Angiv øvrige ting, der lindrer hudlidelsen',
    otherAppearanceChanges: 'Øvrige forandringer i udseende',
    otherDiscomforts: 'Andre gener',
    otherInspections: 'Andre kriterier',
    otherMedicationOrExposure:
      'Angiv ny medicin og/eller eksponering inkl. varighed',
    otherRashAge: 'Anden hudlidelsesalder',
    otherSymptoms: 'Andre symptomer',
    otherTreatmentsFromLesion:
      'Angiv tidligere behandling/vurdering og resultat',
    otherTreatmentsOrTrauma: 'Andre behandlinger',
    pathologySpecimenID: 'Materiale-ID',
    patientInfo: 'Patientinfo',
    pregnancyWithin6Months: 'Graviditet inden for de seneste 6 måneder',
    rateDiagnosticsDifficulty: 'Vurdér diagnostisk sværhedsgrad',
    reasonForConsult: 'Hvad er årsagen til denne konsultation?',
    recentChanges:
      'Nogen nylige forandringer i læsionens størrelse, form, farve, tekstur?',
    referrer: 'Henvisende læge',
    reportedByPatient: 'Rapporteret af patient',
    rescanPathologyID: 'Genscan materiale-ID',
    restoreAccount: 'Gendan konto?',
    riskFactors: 'Risikofaktorer',
    routine: 'Rutine',
    scanSpecimentIDNumber: 'Scan materiale-ID',
    selectAggravatingFactors: 'Ting, der forværrer hudlidelsen',
    selectAlleviatingFactors: 'Ting, der lindrer hudlidelsen',
    selectChanges: 'Vælg forandring(er)',
    selectObjectiveFindings: 'Vælg objektive fund',
    selectRelevant: 'Vælg relevante',
    selectRelevantChanges: 'Vælg de relevante forandringer',
    selectRiskFactors: 'Vælg risikofaktor(er)',
    selectSkinTypeMatch: 'Vælg den hudtype, der passer bedst',
    selectSymptoms: 'Vælg symptom(er)',
    sinceWhen: 'Siden hvornår?',
    skinType: 'Hudtype',
    SSN: 'CPR',
    symptom: 'Symptom',
    symptoms: 'Symptomer',
    symptomsFromLesion:
      'Er der nogen symptomer fra læsionen? (kløe, smerte, blødning etc.)',
    symptomsFromRash:
      'Giver hudlidelsen nogle symptomer? (Fx kløe, ømhed etc.)',
    teleEvaluation: 'Televurdering',
    tentativeClinicalDiagnosis: 'Tentativ klinisk diagnose',
    tentativeDiagnosis: 'Tentative diagnose',
    typeOfCancer: 'Kræfttype',
    typeOfTeledermatology: 'Type teledermatologi',
    typeOfTreatmentOrTrauma: 'Behandlingstype',
    uniqueIdentificationNumber: '{uniqueIdentificationNumber}',
    urgent: 'Akut',
    whenChangesBegin: 'Hvornår begyndte disse forandringer i læsionen?',
    yesSelectChanges: 'Ja - Vælg forandringer',
    yesSelectSymptoms: 'Ja - Vælg symptom(er)',
    yo: 'år',
  },
  diagnosis: {
    carcinoma: 'Carcinom',
    melanoma: 'Melanom',
  },
  errorMessages: {
    anErrorHappened: 'Der opstod en fejl',
    badRequest: 'Forkert anmodning',
    cprLookupDailyLimitReached:
      'Du har nået dagens grænse for CPR-opslag. Prøv igen i morgen, eller kontakt support, hvis du har brug for flere.',
    cprLookupRequiresMitId:
      'Opslag af CPR-nummer kræver login med MitID. Log ud, og log ind igen med MitID.',
    errorLoadingAvatar: 'Fejl ved indlæsning af avatar',
    errorOccurredWhileUpdating: 'Der opstod en fejl under opdateringen',
    invalidId: 'Ikke gyldigt identifikationsnummer (CPR)',
    newHealthRecord: 'Der opstod en fejl under oprettelse af ny patient',
    newPatient: 'Der opstod en fejl under oprettelse af ny patient',
    patientConsent: 'Der opstod en fejl under opdateringen af patientsamtykke',
    somthingWentWrongPleaseContact:
      'Noget gik galt. Vi er ikke helt sikre på, hvad der skete - men prøv venligst at genstarte appen. Hvis problemet fortsætter, kontakt',
    thisIsEmbarassing: 'Dette er pinligt',
    unauthorized: 'Uautoriseret',
  },
  fieldPlaceholders: {
    enterAdditionalDetails: 'Angiv flere detaljer her...',
    enterAnswer: 'Angiv dit svar her...',
    enterNote: 'Skriv din note her...',
    enterOtherAgeOfLesion: 'Angiv beskrivelse af læsionens alder her...',
    enterOtherObjectiveFindingsHere: 'Indtast øvrige objektive fund her...',
    enterOtherRiskFactorsHere: 'Indtast øvrige risikofaktorer her...',
    findADiagnosis: 'Find en diagnose',
    socialSecurityDummy: 'XXXXXX-XXXX',
    typeHereToEnterDescription: 'Indtast beskrivelse her...',
    typeHereToEnterOtherAge: 'Indtast anden alder her...',
    typeHereToEnterOtherChanges: 'Indtast øvrige forandringer her...',
    typeHereToEnterOtherInspections: 'Indtast øvrige kriterier her...',
    typeHereToEnterOtherSymptoms: 'Indtast øvrige symptomer her...',
    typeHereToEnterOtherTreatmentsOrTrauma: 'Indtast øvrige behandlinger...',
  },
  formOptions: {
    imageLocation: {
      closeUpImage: 'Nærbillede',
      dermoscopicImage: 'Dermatoskopisk billede',
      location: 'Placering (3D avatar)',
      overviewImage: 'Overbliksbillede',
    },
    reasonForConsult: {
      diagnosis: 'Hjælp med diagnosen',
      diagnosisAndManagement: 'Begge',
      management: 'Hjælp til den mest hensigtsmæssige håndtering',
    },
  },
  genders: {
    female: 'Kvinde',
    gender: 'Køn',
    male: 'Mand',
    other: 'Andet',
  },
  medicalRecord: {
    emptyPlaceholder: 'Afsluttede registreringer vises her',
    error: {
      editingMedicalRecord: 'Fejl under redigering af journal',
    },
    fieldName: {
      aggravatingFactor: 'Forværrende faktor{{nr:er}}',
      alleviatingFactor: 'Lindrende faktor{{nr:er}}',
      assessmentDifficulty: 'Vurderingens sværhedsgrad',
      breslowThickness: 'Breslow-tykkelse',
      changesInAppearance: 'Forandring{{nr:er}} i udseende',
      changesMadeBy: 'Ændringer foretaget af',
      clinicalAction: 'Klinisk handling',
      clinicalDiagnosis: 'Klinisk diagnose',
      clinicalQuestion: 'Klinisk spørgsmål',
      contactedBy: 'Gennemlæst af',
      diagnosis: 'Diagnose',
      pathologyID: 'Materiale-ID',
      differentialDiagnosis: 'Differentialdiagnose',
      disposition: 'Disponering',
      evaluatedBy: 'Vurderet af',
      familyHistoryOfMelanoma: 'Melanom i familien',
      generalSymptom: '{{Generelt symptom | Generelle symptomer}}',
      hasFormerSkinCancer: 'Tidligere malignt melanom eller hudkræft',
      imageQuality: 'Billedkvalitet',
      immunosuppressantUse: 'Brug af immunsuppressiv medicin',
      lesionAge: 'Læsionens alder',
      lesionThickness: 'Estimeret tykkelse',
      medicationOrExposure: 'Medicin eller eksponering',
      note: 'Henvisningsårsag og yderligere læsionsdetaljer',
      patientResponseNote: 'Patientbesked',
      pcpAssessmentReviewNote: 'Note',
      teleAssessmentNote: 'Anbefaling om opfølgning hos egen læge',
      otherObjectiveFindings:
        'Objektive fund (f.eks., solskadet hud, multiple/særlige typer af nævi)',
      pregnancyWithin6Months: 'Graviditet inden for de seneste 6 måneder',
      previousTreatmentOrTrauma: 'Tidligere behandling',
      primaryDiagnosis: 'Primær diagnose',
      rashAge: 'Hudlidelsens alder',
      recommendedClinicalAction: 'Anbefalet klinisk handling',
      registeredBy: 'Registreret',
      created: 'Oprettet',
      riskFactors:
        'Risiko faktorer (f.eks., soleksponering, immundæmpende behandling, familie historik)',
      secondaryDiagnosis: 'Sekundær diagnose',
      skinType: 'Hudtype',
      symptom: 'Symptom{{nr:er}}',
      tentativeClinicalDiagnosis: 'Tentativ klinisk diagnose',
      tertiaryDiagnosis: 'Tertiær diagnose',
      timeSpentReviewing: 'Tid brugt på gennemgang',
      followUpPriority: 'Opfølgningsprioritet',
      formAnswers: 'Formularsvar',
    },
    fieldValue: {
      age: '(tid: {age})',
      appearanceChanges: '{triageInfo}',
      changedFromOldToNew: 'Ændret fra {old} til {new}',
      closeUpImage: 'Nærbillede',
      dermoscopicImage: 'Dermatoskopisk billede',
      none: 'Ingen',
      notAskFurther: 'Ikke specificeret',
      notDisplayable: 'Kunne ikke vises',
      overviewImage: 'Overbliksbillede',
      photosChanged: '{removed} fjernet, {added} tilføjet',
      timeSpentReviewingMinutes: '{minutes} minut{{ter}}',
      timeSpentIpcBillingParagraph:
        'I spent a total of {minutes} minute{{s}} on this IPC including review of patient data and records and documentation of my assessment. Consult was routed to referring provider.',
      unknownThickness: 'Tumor-tykkelse ukendt (Tx)',
    },
    urgencyDescriptions: {
      none: 'Ingen anbefaling om opfølgning. Fund uden klinisk betydning eller allerede velbehandlet tilstand.',
      routine:
        'Opfølgning i almen praksis uden hast. Kan afvente næste planlagte kontakt.',
      subAcute:
        'Opfølgning i almen praksis bør ske relativt snart, men ikke akut.',
      acute: 'Opfølgning i almen praksis bør ske hurtigst muligt.',
    },
    notRegistered: 'Ikke registreret',
    subtitle: {
      anamnesis: 'Anamnese',
      assessment: 'Vurdering',
      baseline: 'Baseline',
      dermatologicalDiagnosis: 'Ny dermatologisk diagnose',
      histopathologicalAssessment: 'Histopatologisk vurdering',
      histoPathologicalDiagnosis: 'Ny histopatologisk diagnose',
      objective: 'Objektivt',
      patientContacted: 'Gennemlæst svar fra specialist',
      conditionTypeInfoEdited: '{type}sinfo redigeret',
      teledermatologicalAssessment: 'Televurdering',
      teleAssessment: 'Televurdering',
      followUp: '{months} måneder opfølgning',
      eConsult: 'Anmodning om televurdering',
    },
    title: {
      history: 'Historik',
      latestClinicalInformations: 'Seneste kliniske information',
      medicalRecord: 'Journal',
      planAndConclusion: 'Plan og konklusion',
      predispositions: 'Prædispositioner',
      latestDiagnosis: 'Seneste diagnose',
      lesionInformation: 'Læsion information',
      rashInformation: 'Info om hudlidelsen',
      patientInformation: 'Patientinformation',
    },
    by: 'af',
  },
  medicalRecordTitle: {
    baseline: 'Baseline',
    histopatologicalAssessment: 'Histopatologisk vurdering',
    patientContacted: 'Gennemlæst svar fra specialist',
    conditionTypeInfoEdited: '{type}sinfo redigeret',
    teledermatologicalAssessment: 'Televurdering',
    patientInfoEdited: 'Patientsinfo redigeret',
    clinicalActionRemoved: '{action} fjernet',
    clinicalActionCancelled: '{action} annulleret',
  },
  medicalRecordValues: {
    age: '(tid: {age})',
    aggravatingFactor: 'Forværrende faktor{{nr:er}}',
    alleviatingFactor: 'Lindrende faktor{{nr:er}}',
    anamnesis: 'Anamnese',
    appearanceChanges: '{triageInfo}',
    assessmentDifficulty: 'Vurderingens sværhedsgrad',
    changedFromOldToNew: 'Ændret fra {old} til {new}',
    changesInAppearance: 'Forandring{{nr:er}} i udseende',
    changesMadeBy: 'Ændringer foretaget af',
    clinicalAction: 'Klinisk handling',
    clinicalDiagnosis: 'Klinisk diagnose',
    closeUpImage: 'Nærbillede',
    contactedBy: 'Gennemlæst af',
    dermoscopicImage: 'Dermatoskopisk billede',
    differentialDiagnosis: 'Differentialdiagnose',
    evaluatedBy: 'Vurderet af',
    familyHistoryOfMelanoma: 'Melanom i familien',
    generalSymptom: '{{Generelt symptom | Generelle symptomer}}',
    hasFormerSkinCancer: 'Tidligere malignt melanom eller hudkræft',
    history: 'Historik',
    imageQuality: 'Billedkvalitet',
    immunosuppressantUse: 'Brug af immunsuppressiv medicin',
    latestClinicalInformations: 'Seneste kliniske information',
    lesionAge: 'Læsionens alder',
    location: 'Placering',
    medicalRecord: 'Journal',
    medicationOrExposure: 'Medicin eller eksponering',
    none: 'Ingen',
    notAskFurther: 'Ikke specificeret',
    note: 'Note',
    notRegistered: 'Ikke registreret',
    objective: 'Objektivt',
    otherObjectiveFindings: 'Øvrige objektive fund',
    overviewImage: 'Overbliksbillede',
    planAndConclusion: 'Plan og konklusion',
    predispositions: 'Prædispositioner',
    previousTreatmentOrTrauma: 'Tidligere behandling',
    primaryDiagnosis: 'Primær diagnose',
    recommendedClinicalAction: 'Anbefalet klinisk handling',
    riskFactors: 'Risikofaktorer',
    secondaryDiagnosis: 'Sekundær diagnose',
    skinType: 'Hudtype',
    symptom: 'Symptom{{nr:er}}',
    tentativeClinicalDiagnosis: 'Tentativ klinisk diagnose',
    tertiaryDiagnosis: 'Tertiær diagnose',
    timeSpentReviewing: 'Tid brugt på gennemgang',
  },
  medicalUnitPicker: {
    noAccess: {
      logout: 'Log ud',
      support: 'Melatech support:',
      title: 'Velkommen til Dermloop!',
      gainAccess: 'For at få adgang skal du kontakte vores support.',
      sendReactivationEmailAction: 'Send genaktiverings-email',
      superUserRestoreDescription:
        'For at genaktivere din adgang skal du kontakte din superbruger(e):',
      selfReactivateRestoreDescription:
        'For at genaktivere din adgang kan du trykke på knappen herunder.',
      userDeactivated: 'Din adgang (<>{email}<>) er blevet deaktiveret.',
      userHasNoAccess:
        'Din bruger (<>{email}<>) har ikke adgang til nogen klinisk enhed.',
      userInactive:
        'Din adgang (<>{email}<>) er blevet deaktiveret grundet inaktivitet i 90 dage.',
    },
  },
  numberInput: {
    rangeHint: 'Angiv et helt tal fra {min} til {max}',
    minHint: 'Angiv et helt tal på mindst {min}',
    maxHint: 'Angiv et helt tal på højst {max}',
    wholeHint: 'Angiv et helt tal',
  },
  patientInfo: {
    accNo: 'Acc. No.',
    cpr: 'CPR',
    phone: 'Tlf.',
  },
  pendingInvitations: {
    alertBodyText:
      'Åbn venligst den invitation, der er sendt til din e-mail, og klik på linket for at acceptere',
    alertClose: 'Luk',
    alertTitle: 'Du har afventende invitationer',
  },
  plaintext: {
    bad: 'Dårlig',
    baseline: 'Baseline',
    biopsyClinicalActionSelectionOnlyOnMobile:
      'Biopsi som klinisk handling kan KUN vælges fra Dermloop Capture',
    changeIn: 'Forandring i',
    clinicalActionRequiresImages:
      'Denne handling kan først vælges, når de nødvendige billeder er taget.',
    contactMelatechMail: '{email}',
    contactSupport: 'Kontakt support:',
    usCountryCode: '+1',
    euCountryCode: '+45',
    usPhoneLink: 'tel:+18336902903',
    euPhoneLink: 'tel:+4544408245',
    usSupportNumber: '(833) 690-2903',
    euSupportNumber: '44 40 82 45',
    dateOfBirth: 'Fødselsdato',
    downloadImage: 'Download',
    enter: 'Indtast',
    entered: 'Indtastet',
    formerTreatmentOrTrauma: 'Tidligere behandling',
    good: 'God',
    invalid: 'Ugyldig',
    mandatory: 'Obligatorisk',
    no: 'Nej',
    not: 'Ikke',
    nrFormerTreatmenstOrTraumas: '{nr} tidligere behandling{{er}} valgt',
    nrObjectiveFindings: '{nr} {{objektivt|objektive}} fund valgt',
    nrRiskFactors: '{nr} risikofaktor{{er}} valgt',
    okay: 'Okay',
    or: 'Eller',
    orSignIn: 'eller log ind med dit brugernavn og password',
    overTheLast: 'over de sidste',
    requestDeletion: 'Du har anmodet om, at vi sletter din bruger på',
    scanCode: 'Scan denne kode med din telefon for at bekræfte koden:',
    scanned: 'Scannet',
    seeMore: 'Se mere',
    settings: 'Indstillinger',
    sinceUnknow: 'siden ukendt',
    size: 'Størrelse',
    unknown: 'Ukendt',
    unspecified: 'Uspecificeret',
    while: 'imens',
    years: 'År',
    yes: 'Ja',
    yetBeenDeleted:
      'blevet slettet endnu. Du kan derfor stadig gendanne din bruger med al data.',
    yourAccountHas: 'Din bruger er',
  },
  restoreUserRequest: {
    accountNotDeleted:
      'Din konto er <>IKKE<> blevet slettet endnu. Du kan derfor stadig gendanne din bruger med al data.',
    actionText: {
      logOut: 'Log ud',
      restore: 'Gendan',
    },
    requestDate: 'Du har <>{date}<> anmodet om, at vi sletter din konto.',
    title: 'Gendan konto?',
  },
  roles: {
    admin: 'Dermloop Admin',
    editor: 'Editor',
    jurisdictionalGroupInsuranceManager: 'Insurance Manager',
    learnUser: 'Learn-bruger',
    manager: 'Manager',
    medicalUnitManager: 'Superbruger',
    medicalUnitParticipant: 'Kliniker',
    medicalUnitAssistant: 'Medicinsk Assistent',
    medicalUnitPathologist: 'Patolog',
    medicalUnitTeledermatologist: 'Teledermatolog',
    medicalUnitTeledermatologistManager: 'Teledermatolog-ansvarlig',
    researcher: 'Forsker',
    reviewer: 'Reviewer',
    siteManager: 'Site Manager',
    trainingAssessmentExporter: 'Learn Data Eksportør',
  },
  conditionTypes: {
    lesionExamples: 'Nevus, hudkræft etc.',
  },
  statuses: {
    diagnosed: 'Diagnosticeret',
    draft: 'Kladde',
    noAdditionalImages: 'Ingen nye billeder',
    noRegisteredDiagnosis: 'Ingen registreret diagnose',
    nrCloseUpImages: '{nr} nærbillede{{r}}',
    nrDermoscopicImages: '{nr} dermatoskopisk{{e}} billede{{r}}',
    nrOverviewImages: '{nr} overbliksbillede{{r}}',
    overdueFollowup: 'Overskredet opfølgning',
    pendingDiagnostic: 'Afventer diagnose',
    undiagnosable: 'Udiagnosticérbar',
  },
  statusMessages: {
    invalidSSN: 'Ugyldigt CPR',
    signedInAs: 'Du er logget ind som:',
    switchAccount: 'Skift bruger',
    unsavedChanges: 'Ugemte ændringer',
    welcomeBack: 'Velkommen tilbage',
  },
  tooltips: {
    selectMultiple: 'Vælg flere',
  },
  email: {
    linkToDermloop: 'Til Dermloop',
    unitBusinessHours: 'arbejdstime{{hours:r}}',
    unitHours: 'time{{hours:r}}',
    preReassignmentWarning: {
      titleOne: 'Sag bliver omfordelt snart',
      titleMany: 'Sager bliver omfordelt snart',
      bodyOneSoon:
        'Vi har registreret at du har 1 uafsluttet sag. Den bliver omfordelt om under {hours} {unit}. Klik på knappen nedenfor for at gå til dine sager.',
      bodyManySoon:
        'Vi har registreret at du har {count} uafsluttede sager. Den første bliver omfordelt om under {hours} {unit}. Klik på knappen nedenfor for at gå til dine sager.',
      bodyOneImminent:
        'Vi har registreret at du har 1 uafsluttet sag. Den bliver omfordelt med det samme. Klik på knappen nedenfor for at gå til dine sager.',
      bodyManyImminent:
        'Vi har registreret at du har {count} uafsluttede sager. Den første bliver omfordelt med det samme. Klik på knappen nedenfor for at gå til dine sager.',
    },
    reassignmentAlert: {
      title: '{count} sag{{count:er}} viderefordelt i {federationName}',
      body: '{count} sag{{count:er}} er blevet viderefordelt siden {cutoff}.',
      sendersHeading: 'Sagerne lå hos:',
      senderCaseCount: '{count} sag{{count:er}}',
      senderOverflow: 'og {count} {{count:anden|andre}}',
      senderUnknown: 'Ukendt afsender',
    },
  },
  sms: {
    patientResponseReady:
      'Dit svar på din hudlægevurdering hos Dermloop er nu klar. Klik på linket herunder for at se svaret. Du skal verificere dig med dit CPR-nummer. Linket er aktivt i 30 dage. Har du spørgsmål, er du velkommen til at kontakte NurseCare på tlf. +45 70 20 22 32 eller e-mail info@nursecare.dk',
  },
  warningMessages: {
    delete: 'Slet',
    discard: 'Kassér',
    leaveWithoutSaving: 'Forlad uden at gemme',
    leaveWithoutSavingWarning:
      'Du er ved at forlade siden. Vil du forlade siden uden at gemme?',
  },
} satisfies NamespaceSharedTranslation;

export default da_shared;
